"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

/* PDP funcional com produto real de loja Shoppub (Larulp, hotlink
   cdn.shoppub.io), fiel à PDP original: galeria com setas, tamanhos
   P–GG, 10×, "Comprar pelo WhatsApp" e chips de tecnologia do tecido.
   Ao adicionar à sacola, um push confirma — usabilidade ao vivo. */

const CDN = "https://cdn.shoppub.io/cdn-cgi/image";
const P = (size: number, path: string) =>
  `${CDN}/w=${size},h=${size},q=80,f=auto/larulp/media/uploads/produtos/foto/${path}`;

/* cores reais do produto (produtos irmãos na loja), fotos reais de cada */
const CORES = [
  {
    id: "bege",
    label: "Bege Canela",
    hex: "#c9ab96",
    fotos: ["shhrfhss/whatsapp-image-2026-08-20-at-084925-1.jpeg"],
  },
  {
    id: "branco",
    label: "Branco",
    hex: "#f6f6f4",
    fotos: ["ajjpmkoz/1938024_0012012061_002_03.jpg"],
  },
  {
    id: "preto",
    label: "Preto",
    hex: "#101012",
    /* ordem real da galeria do produto preto */
    fotos: [
      "jklmbgls/para-uma-vida-em-movimento-larulp_oficial-2.jpg",
      "lrxzruat/2012061_001_01.jpg",
      "flbhrmuw/2012061_001_02.jpg",
      "aesuqumg/2012061_001_03.jpg",
      "lygeyaoj/para-uma-vida-em-movimento-larulp_oficial-4.jpg",
    ],
  },
];

const TAMANHOS = ["P", "M", "G", "GG"];
const TECNOLOGIA = ["Proteção UV50+", "Dryeasy", "Tecido stretch", "Controle de odor"];

function Seta({ dir, onClick }: { dir: "prev" | "next"; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={dir === "prev" ? "Foto anterior" : "Próxima foto"}
      onClick={onClick}
      className={`absolute top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-paper/90 text-ink shadow-[0_6px_18px_-6px_rgba(10,10,11,0.35)] backdrop-blur transition hover:bg-paper ${
        dir === "prev" ? "left-3" : "right-3"
      }`}
    >
      <svg viewBox="0 0 24 24" className={`h-4 w-4 ${dir === "prev" ? "-translate-x-px rotate-180" : "translate-x-px"}`} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

export default function MockPdp() {
  const [corIdx, setCorIdx] = useState(2); // preto, como na PDP original
  const [foto, setFoto] = useState(0);
  const [tam, setTam] = useState(1);
  const [push, setPush] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const cor = CORES[corIdx];
  const FOTOS = cor.fotos;
  const i = Math.min(foto, FOTOS.length - 1);

  const adicionar = () => {
    setPush(false);
    // reinicia a animação mesmo em cliques seguidos
    requestAnimationFrame(() => requestAnimationFrame(() => setPush(true)));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPush(false), 2800);
  };

  return (
    <div className="select-none rounded-card border border-line bg-paper p-5 shadow-[0_30px_70px_-40px_rgba(10,10,11,0.4)] sm:p-6">
      {/* galeria: foto grande com setas + dots */}
      <div className="relative overflow-hidden rounded-2xl bg-mist">
        <img
          key={FOTOS[i]}
          src={P(1000, FOTOS[i])}
          alt={`Camisa Winnie Movetech — ${cor.label}`}
          className="aspect-[3/4] w-full object-cover object-[50%_30%]"
        />
        {FOTOS.length > 1 && (
          <>
            <Seta dir="prev" onClick={() => setFoto((i - 1 + FOTOS.length) % FOTOS.length)} />
            <Seta dir="next" onClick={() => setFoto((i + 1) % FOTOS.length)} />
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
              {FOTOS.map((f, d) => (
                <span
                  key={f}
                  className={`h-1.5 rounded-full transition-all ${
                    d === i ? "w-5 bg-ink" : "w-1.5 bg-ink/25"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="mt-5">
        <div className="text-[12px] font-semibold text-smoke underline underline-offset-2">
          Seja o primeiro a avaliar
        </div>
        <div className="mt-1 font-display text-xl font-bold leading-tight">
          Camisa Winnie Movetech — {cor.label}
        </div>
        <div className="text-[13px] text-smoke">Camisa fitness feminina 89% poliamida</div>
        <div className="mt-2">
          <span className="font-display text-2xl font-bold">R$ 699,90</span>
          <div className="text-[12px] text-smoke">ou 10× de R$ 69,99</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-[auto_1fr] items-start gap-6">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-smoke">Cores</div>
          <div className="mt-2 flex gap-2">
            {CORES.map((c, d) => (
              <button
                type="button"
                key={c.id}
                aria-label={`Cor ${c.label}`}
                onClick={() => {
                  setCorIdx(d);
                  setFoto(0);
                }}
                className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition ${
                  d === corIdx ? "ring-2 ring-ink ring-offset-2" : "hover:ring-2 hover:ring-line hover:ring-offset-2"
                }`}
              >
                <span
                  style={{ background: c.hex }}
                  className="h-7 w-7 rounded-full border border-ink/15"
                />
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-smoke">
              Tamanho
            </span>
            <span className="cursor-pointer text-[12px] font-semibold text-ink/70 underline underline-offset-2 hover:text-ink">
              Tabela de medidas
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {TAMANHOS.map((t, d) => (
              <button
                type="button"
                key={t}
                onClick={() => setTam(d)}
                className={`flex h-9 w-10 cursor-pointer items-center justify-center rounded-full border text-[12px] font-bold transition ${
                  d === tam ? "border-ink bg-ink text-lime" : "border-line text-ink/70 hover:border-ink"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-smoke">
          Tecnologia
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {TECNOLOGIA.map((t) => (
            <span key={t} className="rounded-full bg-mist px-3 py-1.5 text-[12px] font-medium text-ink/75">
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="relative mt-5">
        {/* push de confirmação — sobe de cima do botão */}
        <div
          aria-live="polite"
          className={`pointer-events-none absolute inset-x-0 bottom-full mb-3 flex items-center gap-3 rounded-2xl border border-line bg-paper/95 p-3 shadow-[0_14px_30px_-14px_rgba(10,10,11,0.45)] backdrop-blur transition-all duration-500 [transition-timing-function:cubic-bezier(.22,1,.36,1)] ${
            push ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ink p-2">
            <Image src="/brand/shoppub_S_branco.svg" alt="" width={16} height={24} className="h-5 w-auto" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-bold">Adicionado à sacola</span>
            <span className="block truncate text-[11px] text-smoke">
              Camisa Winnie Movetech · {cor.label} · Tam {TAMANHOS[tam]}
            </span>
          </span>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="#0a0a0b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5l4.2 4.2L19 7" />
            </svg>
          </span>
        </div>

        <button
          type="button"
          onClick={adicionar}
          className="w-full cursor-pointer rounded-full bg-lime py-3.5 text-center text-sm font-bold text-ink transition hover:bg-lime-deep"
        >
          Adicionar à sacola
        </button>
      </div>
      <div className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-line py-3 text-center text-sm font-bold text-ink/80 transition hover:border-ink">
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        Comprar pelo WhatsApp
      </div>
    </div>
  );
}
